import React, { useMemo, useState } from 'react';
import { CircularProgress } from '@mui/material';
import dayjs from 'dayjs';
import clsx from 'clsx';

import { translateMinToHHhMMmString } from '../../../../data-access';
import { getTaskToBeScheduled, ScheduledTaskInfoModal } from '../../common';
import './SchedulerTaskPref.scss';
import { TaskItemForScheduleWithSelected } from '../reducer';
import { SchedulerPrefCheckItem } from '../SchedulerPrefCheckItem';
import { getDueDateYYYYMMDD, sortTasksByDateAndPriority } from '../../../../helpers';
import { formatTimeFromDayjs } from '../../../../helpers/time.helper';
import { useGeneralSettings } from '../../../../hooks';
import { useTranslation } from 'react-i18next';

interface TasksPrefItemState {
  [key: string]: TaskItemForScheduleWithSelected;
}

interface SchedulerTaskPrefProps {
  tasks: TasksPrefItemState;
  isLoading?: boolean;
  onTaskChange: (taskId: string, pref: Partial<TaskItemForScheduleWithSelected>) => void;
  targetDate: string;
  shouldShowConnectTodoBtn?: boolean;
  className?: string;
  onCheckTask: (taskId: string, enabled: boolean) => void;
  hasConfigured?: boolean;
}

const TASK_MAX_DISPLAY_COUNT = 10;

export const SchedulerTaskPref: React.FC<SchedulerTaskPrefProps> = ({
  tasks,
  isLoading,
  onTaskChange,
  targetDate,
  shouldShowConnectTodoBtn = false,
  className,
  onCheckTask,
  hasConfigured,
}) => {
  const { t } = useTranslation();
  const {
    generalSettings: { timeFormat },
  } = useGeneralSettings();
  const [selectedTask, setSelectedTask] = useState<TaskItemForScheduleWithSelected | null>(null);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [showAllTasks, setShowAllTasks] = useState(false);
  const handleClickTask = (task: TaskItemForScheduleWithSelected, taskId: string) => {
    setSelectedTask(task);
    setSelectedTaskId(taskId);
  };

  const handleCloseModal = () => {
    setSelectedTask(null);
    setSelectedTaskId(null);
  };

  const handleTaskChange = (taskId: string, updatedTask: TaskItemForScheduleWithSelected) => {
    onTaskChange(taskId, updatedTask);
  };

  const handleToggleShowMore = () => {
    setShowAllTasks(!showAllTasks);
  };

  // Sorting: selectedDate -> overdueDate (closest first) -> futureDate -> no due date
  const sortedTasks = useMemo(() => {
    return sortTasksByDateAndPriority(
      Object.entries(tasks),
      targetDate,
      (task, date) => getTaskToBeScheduled(task, date).canTaskBeScheduled
    );
  }, [tasks, targetDate]);

  const hasMoreTasks = sortedTasks.length > TASK_MAX_DISPLAY_COUNT;
  const displayedTasks = showAllTasks ? sortedTasks : sortedTasks.slice(0, TASK_MAX_DISPLAY_COUNT);

  return (
    <div className={`scheduler-task-pref ${className || ''}`}>
      {shouldShowConnectTodoBtn ? (
        <div className="scheduler-task-pref__list">
          <p className="scheduler-task-pref__no-connect-text">
            {t('task.scheduler.taskPref.noTasksAdd')}
          </p>
        </div>
      ) : (
        <div className={clsx('scheduler-task-pref__list', 'scheduler-task-pref__list--ff')}>
          {isLoading ? (
            <div className="scheduler-task-pref__loading">
              <CircularProgress size={24} />
            </div>
          ) : Object.keys(tasks).length ? (
            <div className="scheduler-task-pref__tasks-container">
              {displayedTasks.map(([taskId, task]) => {
                const { canTaskBeScheduled, duration, hasScheduledAlready } = getTaskToBeScheduled(
                  task,
                  targetDate
                );
                // Calculate start and end times from task's dueDateTime and duration (similar to KanbanTaskCard)
                let startTime: string | undefined;
                let endTime: string | undefined;

                if (task.dueDateTime?.datetime) {
                  const startDatetime = dayjs(task.dueDateTime.datetime);
                  startTime = formatTimeFromDayjs(startDatetime, timeFormat);

                  if (task.duration?.amount) {
                    const endDatetime = startDatetime.add(
                      task.duration.amount,
                      task.duration.unit || 'minute'
                    );
                    endTime = formatTimeFromDayjs(endDatetime, timeFormat);
                  }
                }

                return (
                  <SchedulerPrefCheckItem
                    key={taskId}
                    title={task.name}
                    duration={duration ? `${translateMinToHHhMMmString(duration)}` : ''}
                    startTime={startTime}
                    endTime={endTime}
                    date={getDueDateYYYYMMDD(task.dueDateTime)}
                    checked={task.selected || false}
                    disabled={hasConfigured}
                    onClick={() => handleClickTask(task, taskId)}
                    onCheckChange={(checked) => onCheckTask(taskId, checked)}
                    infoIconType={hasScheduledAlready ? 'calendar' : 'info'}
                  />
                );
              })}
              {hasMoreTasks && (
                <button
                  className="scheduler-task-pref__show-more-btn"
                  onClick={handleToggleShowMore}
                  type="button"
                >
                  {showAllTasks
                    ? t('task.scheduler.taskPref.showLess')
                    : t('task.scheduler.taskPref.showMore', {
                        count: sortedTasks.length - TASK_MAX_DISPLAY_COUNT,
                      })}
                </button>
              )}
            </div>
          ) : (
            <p className="scheduler-task-pref__no-connect-text">
              {t('task.scheduler.taskPref.noTasksDueDates')}
            </p>
          )}
        </div>
      )}

      {selectedTask && selectedTaskId && (
        <ScheduledTaskInfoModal
          open={!!selectedTask}
          targetDate={targetDate}
          task={selectedTask}
          taskId={selectedTaskId}
          onItemChange={handleTaskChange}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};

export default SchedulerTaskPref;
